import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { firestore } from "./firebase_config";
import type { FaqConfig, FaqContentItem, FaqSection } from "src/types/faq";
import {
  appendActivity,
  ActivityDomains,
  type ActivityActor,
  type ActivityAppendInput,
} from "./activityService";

/** Single FAQ document — readable by mobile clients; writable by dashboard roles (see `firestore.rules`). */
export const FAQ_COLLECTION = "appContent";
export const FAQ_DOCUMENT_ID = "faq";

const faqDocRef = () => doc(firestore, FAQ_COLLECTION, FAQ_DOCUMENT_ID);

/** Full path string for UI / audit (Firestore). */
export const FAQ_FIRESTORE_PATH = `${FAQ_COLLECTION}/${FAQ_DOCUMENT_ID}`;

export const DEFAULT_FAQ_CONFIG: FaqConfig = { faq: [] };

function normalizeContentItem(raw: unknown): FaqContentItem {
  if (!raw || typeof raw !== "object") {
    return { subTitle: "", description: "" };
  }
  const o = raw as Record<string, unknown>;
  return {
    subTitle:
      typeof o.subTitle === "string" ? o.subTitle : String(o.subTitle ?? ""),
    description:
      typeof o.description === "string"
        ? o.description
        : String(o.description ?? ""),
  };
}

function normalizeSection(raw: unknown, fallbackIndex: number): FaqSection {
  if (!raw || typeof raw !== "object") {
    return {
      id: String(fallbackIndex + 1),
      title: "",
      content: [],
    };
  }
  const o = raw as Record<string, unknown>;
  const idRaw = o.id;
  const id =
    typeof idRaw === "string" && idRaw.trim().length > 0
      ? idRaw.trim()
      : String(fallbackIndex + 1);
  const title = typeof o.title === "string" ? o.title : String(o.title ?? "");
  const contentRaw = o.content;
  const content: FaqContentItem[] = Array.isArray(contentRaw)
    ? contentRaw.map((c) => normalizeContentItem(c))
    : [];
  return { id, title, content };
}

export function normalizeFaqConfig(raw: unknown): FaqConfig {
  if (!raw || typeof raw !== "object") {
    return { faq: [] };
  }
  const o = raw as Record<string, unknown>;
  const faqRaw = o.faq;
  if (!Array.isArray(faqRaw)) {
    return { faq: [] };
  }
  return {
    faq: faqRaw.map((s, i) => normalizeSection(s, i)),
  };
}

async function logActivitySafe(entry: ActivityAppendInput): Promise<void> {
  try {
    await appendActivity(entry);
  } catch (e) {
    console.error("Failed to record activity:", e);
  }
}

export async function getFaqConfig(): Promise<FaqConfig> {
  const snap = await getDoc(faqDocRef());
  if (!snap.exists()) {
    return { ...DEFAULT_FAQ_CONFIG };
  }
  return normalizeFaqConfig(snap.data());
}

function countEntries(cfg: FaqConfig): number {
  return cfg.faq.reduce((n, s) => n + s.content.length, 0);
}

export async function saveFaqConfig(
  data: FaqConfig,
  actor?: ActivityActor | null,
): Promise<void> {
  const normalized = normalizeFaqConfig(data);
  const beforeSnapshot = actor ? await getFaqConfig() : null;

  await setDoc(faqDocRef(), {
    faq: normalized.faq,
    updatedAt: serverTimestamp(),
  });

  if (actor && beforeSnapshot !== null) {
    const sectionCount = normalized.faq.length;
    const entryCount = countEntries(normalized);
    await logActivitySafe({
      domain: ActivityDomains.Faqs,
      action: "update",
      resourceType: "faq_config",
      resourceId: FAQ_FIRESTORE_PATH,
      summary: `In-app FAQ updated (${sectionCount} categories, ${entryCount} entries)`,
      actor,
      details: {
        sectionCount,
        entryCount,
        categoryTitles: normalized.faq.map((s) => s.title).filter(Boolean),
      },
    });
  }
}
